import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Briefcase, 
  Clock, 
  Users, 
  DollarSign, 
  TrendingUp, 
  Activity,
  Calendar,
  MapPin,
  AlertTriangle,
  CheckCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveProjectTracking = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTimesheets, setProjectTimesheets] = useState([]);
  const [projectSummary, setProjectSummary] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails();
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      
      if (!selectedProject && response.data.length > 0) {
        setSelectedProject(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async () => {
    if (!selectedProject) return;
    
    try {
      setLoading(true);
      
      // Get timesheet entries for the project (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const timesheetsResponse = await axios.get(`${API}/timesheets/project/${selectedProject.id}`, {
        params: {
          start_date: thirtyDaysAgo.toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0]
        }
      });
      
      setProjectTimesheets(timesheetsResponse.data);
      
      // Calculate project summary
      calculateProjectSummary(timesheetsResponse.data);
      
    } catch (error) {
      console.error('Error fetching project details:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const calculateProjectSummary = (timesheets) => {
    const today = new Date().toISOString().split('T')[0];
    const thisWeekStart = new Date();
    thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
    const thisWeekStartStr = thisWeekStart.toISOString().split('T')[0];

    // Calculate totals
    const totalHours = timesheets.reduce((sum, t) => sum + t.total_hours, 0);
    const totalRegularHours = timesheets.reduce((sum, t) => sum + t.regular_hours, 0);
    const totalOvertimeHours = timesheets.reduce((sum, t) => sum + t.overtime_hours, 0);
    const totalDoubleTimeHours = timesheets.reduce((sum, t) => sum + t.double_time_hours, 0);
    const totalLaborCost = timesheets.reduce((sum, t) => sum + t.total_pay, 0);

    // Today's work
    const todaysEntries = timesheets.filter(t => t.work_date === today);
    const todaysHours = todaysEntries.reduce((sum, t) => sum + t.total_hours, 0);
    const activeWorkersToday = [...new Set(todaysEntries.map(t => t.employee_id))].length;

    // This week's work
    const thisWeekEntries = timesheets.filter(t => t.work_date >= thisWeekStartStr);
    const thisWeekHours = thisWeekEntries.reduce((sum, t) => sum + t.total_hours, 0);
    const uniqueWorkersThisWeek = [...new Set(thisWeekEntries.map(t => t.employee_id))].length;

    // Worker performance
    const workerStats = {};
    timesheets.forEach(entry => {
      if (!workerStats[entry.employee_id]) {
        workerStats[entry.employee_id] = {
          name: entry.employee_name,
          totalHours: 0,
          totalPay: 0,
          daysWorked: 0,
          entries: []
        };
      }
      workerStats[entry.employee_id].totalHours += entry.total_hours;
      workerStats[entry.employee_id].totalPay += entry.total_pay;
      workerStats[entry.employee_id].entries.push(entry);
    });

    // Calculate days worked for each worker
    Object.keys(workerStats).forEach(workerId => {
      const uniqueDates = [...new Set(workerStats[workerId].entries.map(e => e.work_date))];
      workerStats[workerId].daysWorked = uniqueDates.length;
    });

    // Daily productivity (last 7 days)
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayEntries = timesheets.filter(t => t.work_date === dateStr);
      const dayHours = dayEntries.reduce((sum, t) => sum + t.total_hours, 0);
      const dayWorkers = [...new Set(dayEntries.map(t => t.employee_id))].length;
      
      last7Days.push({
        date: dateStr,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        hours: dayHours,
        workers: dayWorkers
      });
    }

    setProjectSummary({
      totalHours,
      totalRegularHours,
      totalOvertimeHours,
      totalDoubleTimeHours,
      totalLaborCost,
      todaysHours,
      activeWorkersToday,
      thisWeekHours,
      uniqueWorkersThisWeek,
      workerStats: Object.values(workerStats),
      last7Days
    });
  };

  const getProjectStatusColor = (status) => {
    const colors = {
      planning: 'bg-yellow-600',
      mobilization: 'bg-blue-600',
      in_progress: 'bg-green-600',
      on_hold: 'bg-orange-600',
      substantially_complete: 'bg-purple-600',
      completed: 'bg-emerald-600',
      cancelled: 'bg-red-600'
    };
    return colors[status] || 'bg-slate-200';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateBudgetProgress = () => {
    if (!selectedProject || !projectSummary) return 0;
    if (selectedProject.estimated_budget === 0) return 0;
    return Math.min((projectSummary.totalLaborCost / selectedProject.estimated_budget) * 100, 100);
  };

  const refresh = () => {
    fetchProjectDetails();
    toast.success('Project data refreshed');
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Live Project Tracking</h1>
            <p className="text-slate-500">Real-time labor tracking and project progress</p>
          </div>
          <Button
            onClick={refresh}
            variant="outline"
            className="border-blue-500 text-blue-700 hover:bg-blue-900"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Project Selector */}
        <div className="lg:col-span-1">
          <Card className="card">
            <CardHeader className="card-header">
              <CardTitle className="text-slate-900 flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-blue-500" />
                Projects
              </CardTitle>
            </CardHeader>
            <CardContent className="card-body">
              <div className="space-y-2">
                {projects.map(project => (
                  <div
                    key={project.id}
                    onClick={() => setSelectedProject(project)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedProject?.id === project.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-300 hover:border-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-slate-900 text-sm">{project.title}</h4>
                      <Badge className={`${getProjectStatusColor(project.status)} text-slate-900`}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{project.client_name}</p>
                    <div className="text-xs text-slate-500">
                      {formatCurrency(project.estimated_budget)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {selectedProject && projectSummary && (
            <>
              {/* Project Overview */}
              <Card className="card">
                <CardHeader className="card-header">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900">{selectedProject.title}</CardTitle>
                    <Badge className={`${getProjectStatusColor(selectedProject.status)} text-slate-900`}>
                      {selectedProject.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <p className="text-slate-500">{selectedProject.client_name}</p>
                </CardHeader>
                <CardContent className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-slate-100 p-3 rounded-lg">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-blue-700 mr-2" />
                        <div>
                          <p className="text-slate-500 text-sm">Total Hours</p>
                          <p className="text-slate-900 font-semibold">{projectSummary.totalHours.toFixed(1)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-100 p-3 rounded-lg">
                      <div className="flex items-center">
                        <DollarSign className="h-5 w-5 text-green-700 mr-2" />
                        <div>
                          <p className="text-slate-500 text-sm">Labor Cost</p>
                          <p className="text-slate-900 font-semibold">{formatCurrency(projectSummary.totalLaborCost)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-100 p-3 rounded-lg">
                      <div className="flex items-center">
                        <Activity className="h-5 w-5 text-orange-700 mr-2" />
                        <div>
                          <p className="text-slate-500 text-sm">Today's Hours</p>
                          <p className="text-slate-900 font-semibold">{projectSummary.todaysHours.toFixed(1)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-slate-100 p-3 rounded-lg">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 text-purple-700 mr-2" />
                        <div>
                          <p className="text-slate-500 text-sm">Active Today</p>
                          <p className="text-slate-900 font-semibold">{projectSummary.activeWorkersToday} workers</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Budget Progress */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-700">Labor Budget Progress</span>
                      <span className="text-slate-500">{calculateBudgetProgress().toFixed(1)}%</span>
                    </div>
                    <Progress value={calculateBudgetProgress()} className="w-full" />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>{formatCurrency(projectSummary.totalLaborCost)}</span>
                      <span>{formatCurrency(selectedProject.estimated_budget)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Labor Breakdown & Daily Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Labor Hours Breakdown */}
                <Card className="card">
                  <CardHeader className="card-header">
                    <CardTitle className="text-slate-900 flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-blue-500" />
                      Labor Hours Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="card-body">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-700">Regular Hours</span>
                        <div className="text-right">
                          <div className="text-slate-900 font-mono">{projectSummary.totalRegularHours.toFixed(1)}h</div>
                          <div className="text-xs text-slate-500">
                            {formatCurrency(projectSummary.totalRegularHours * 65)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-slate-700">Overtime Hours (1.5x)</span>
                        <div className="text-right">
                          <div className="text-orange-700 font-mono">{projectSummary.totalOvertimeHours.toFixed(1)}h</div>
                          <div className="text-xs text-slate-500">
                            {formatCurrency(projectSummary.totalOvertimeHours * 65 * 1.5)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-slate-700">Double Time Hours (2x)</span>
                        <div className="text-right">
                          <div className="text-red-600 font-mono">{projectSummary.totalDoubleTimeHours.toFixed(1)}h</div>
                          <div className="text-xs text-slate-500">
                            {formatCurrency(projectSummary.totalDoubleTimeHours * 65 * 2)}
                          </div>
                        </div>
                      </div>
                      
                      <hr className="border-slate-300" />
                      
                      <div className="flex justify-between items-center font-semibold">
                        <span className="text-slate-900">Total</span>
                        <div className="text-right">
                          <div className="text-green-700 font-mono">{projectSummary.totalHours.toFixed(1)}h</div>
                          <div className="text-sm text-green-700">
                            {formatCurrency(projectSummary.totalLaborCost)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Last 7 Days Activity */}
                <Card className="card">
                  <CardHeader className="card-header">
                    <CardTitle className="text-slate-900 flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-blue-500" />
                      Last 7 Days Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="card-body">
                    <div className="space-y-3">
                      {projectSummary.last7Days.map(day => (
                        <div key={day.date} className="flex justify-between items-center">
                          <div>
                            <span className="text-slate-700">{day.dayName}</span>
                            <span className="text-xs text-slate-500 ml-2">{formatDate(day.date)}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-slate-900 font-mono">{day.hours.toFixed(1)}h</div>
                            <div className="text-xs text-slate-500">{day.workers} workers</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Worker Performance */}
              <Card className="card">
                <CardHeader className="card-header">
                  <CardTitle className="text-slate-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-blue-500" />
                    Worker Performance (Last 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent className="card-body">
                  <div className="space-y-3">
                    {projectSummary.workerStats.map(worker => (
                      <div key={worker.name} className="p-3 bg-slate-100 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-medium text-slate-900">{worker.name}</h4>
                            <p className="text-sm text-slate-500">{worker.daysWorked} days worked</p>
                          </div>
                          <div className="text-right">
                            <div className="text-green-700 font-mono">{worker.totalHours.toFixed(1)}h</div>
                            <div className="text-sm text-slate-500">{formatCurrency(worker.totalPay)}</div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-slate-500">
                            Avg: {(worker.totalHours / worker.daysWorked).toFixed(1)}h per day
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {loading && (
            <Card className="card">
              <CardContent className="card-body">
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 text-slate-500 mx-auto mb-4 animate-spin" />
                  <p className="text-slate-500">Loading project data...</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveProjectTracking;